import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';
import { clientDataCache, getCurrentUserIdFromStorage } from '../../utils/clientDataCache';

function persistSavedJobsSnapshot(state) {
    const uid = getCurrentUserIdFromStorage();
    if (uid && Array.isArray(state.savedJobs) && state.savedJobs.length) {
        clientDataCache.writeSavedJobs(uid, state.savedJobs);
    }
}

export const saveJob = createAsyncThunk('savedJobs/saveJob', async ({ userId, jobId }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token")

        const res = await api.post(`/api/savedjobs/savejob`,
            { userId, jobId },
            {
                headers: {
                    "auth-token": token
                }
            }
        );

        return res.data;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'savedJobsError saving job');
    }
}
);

export const saveMultipleJobs = createAsyncThunk('savedJobs/saveMultipleJobs', async ({ userId, jobs, recruiterData }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const res = await api.post(`/api/savedjobs/savemultiple`,
            { userId, jobs, recruiterData }, {
            headers: {
                "auth-token": token
            }
        });

        return res.data;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'savedJobsError saving jobs');
    }
}
);

export const fetchSavedJobs = createAsyncThunk('savedJobs/fetchSavedJobs', async (userId, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const res = await api.get(`/api/savedjobs/getsavedjobs/${userId}`, {
            headers: {
                "auth-token": token
            },
            // Saved jobs can change immediately after selecting/deploying strategies.
            // Ensure we don't reuse an in-flight GET response from earlier navigation.
            skipDeduplication: true
        });

        return res.data;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'savedJobsError fetching saved jobs');
    }
}
);

export const deleteSavedJob = createAsyncThunk('savedJobs/deleteSavedJob', async (savedJobId, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        await api.delete(`/api/savedjobs/deletesavedjob/${savedJobId}`, {
            headers: {
                "auth-token": token
            }
        });

        return savedJobId;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'savedJobsError deleting saved job');
    }
}
);

// Persist a saved job's pipeline stage (saved | applied | interviewing | offer).
export const updateSavedJobStage = createAsyncThunk('savedJobs/updateSavedJobStage', async ({ savedJobId, stage }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");
        const res = await api.patch(`/api/savedjobs/stage/${savedJobId}`, { stage }, {
            headers: { "auth-token": token }
        });
        return res.data?.data || { _id: savedJobId, stage };
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'Failed to update job stage');
    }
});

// checkJobIsSaved removed — route /is-saved never existed.
// The saved state is checked client-side via savedJobs.some(j => j.jobId?._id === jobId).

export const selectJobStrategy = createAsyncThunk('savedJobs/selectJobStrategy', async ({ userId, jobId, strategyId, subStrategyKey = null, contextSnapshot = null }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const res = await api.post(`/api/savedjobs/select-strategy`,
            { userId, jobId, strategyId, subStrategyKey, contextSnapshot },
            {
                headers: {
                    "auth-token": token
                }
            }
        );

        const savedJob = res.data.savedJob;
        const uid =
            userId ||
            thunkAPI.getState()?.auth?.user?._id ||
            thunkAPI.getState()?.auth?.user?.id;
        if (uid) {
            thunkAPI.dispatch(fetchSavedJobs(uid));
        }

        return savedJob;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error selecting strategy');
    }
}
);

export const clearJobStrategy = createAsyncThunk('savedJobs/clearJobStrategy', async ({ userId, jobId }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");
        const res = await api.post(`/api/savedjobs/clear-strategy`, { userId, jobId }, {
            headers: { "auth-token": token }
        });

        const savedJob = res.data.savedJob;
        const uid =
            userId ||
            thunkAPI.getState()?.auth?.user?._id ||
            thunkAPI.getState()?.auth?.user?.id;
        if (uid) {
            thunkAPI.dispatch(fetchSavedJobs(uid));
        }

        return savedJob;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error clearing strategy');
    }
});

const savedJobsSlice = createSlice({
    name: 'savedJobs',
    initialState: {
        savedJobs: [],
        savedJobsLoading: false,
        savedJobsError: null,
        isJobSaved: false,
    },
    reducers: {
        resetSavedJobsState: (state) => {
            state.savedJobsLoading = false;
            state.savedJobsError = null;
            state.isJobSaved = false;
        },
        setDemoData: (state, action) => {
            state.savedJobs = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(saveJob.pending, (state) => {
                state.savedJobsLoading = true;
                state.savedJobsError = null;
            })
            .addCase(saveJob.fulfilled, (state, action) => {
                state.savedJobsLoading = false;
                state.savedJobs.push(action.payload);
                persistSavedJobsSnapshot(state);
            })
            .addCase(saveJob.rejected, (state, action) => {
                state.savedJobsLoading = false;
                state.savedJobsError = action.payload;
            })

            .addCase(updateSavedJobStage.fulfilled, (state, action) => {
                const { _id, stage } = action.payload || {};
                const entry = state.savedJobs.find(j => String(j._id) === String(_id));
                if (entry) entry.stage = stage;
                persistSavedJobsSnapshot(state);
            })

            .addCase(saveMultipleJobs.pending, (state) => {
                state.savedJobsLoading = true;
                state.savedJobsError = null;
            })
            .addCase(saveMultipleJobs.fulfilled, (state, action) => {
                // insertedJobs are raw DB docs (no populated jobId/companyId).
                // We push them temporarily so the UI isn't blank, but a
                // fetchSavedJobs dispatch (triggered in the component) will
                // replace them with fully-populated documents.
                const newJobs = action.payload.insertedJobs || [];
                state.savedJobs.push(...newJobs);
                state.savedJobsLoading = false;
            })
            .addCase(saveMultipleJobs.rejected, (state, action) => {
                state.savedJobsLoading = false;
                state.savedJobsError = action.payload;
            })

            .addCase(fetchSavedJobs.pending, (state) => {
                state.savedJobsLoading = true;
                state.savedJobsError = null;
                if (state.savedJobs.length === 0) {
                    const uid = getCurrentUserIdFromStorage();
                    const cached = uid ? clientDataCache.readSavedJobs(uid) : null;
                    if (cached?.length) {
                        state.savedJobs = cached;
                    }
                }
            })
            .addCase(fetchSavedJobs.fulfilled, (state, action) => {
                state.savedJobsLoading = false;
                if (Array.isArray(action.payload)) {
                    state.savedJobs = action.payload;
                    persistSavedJobsSnapshot(state);
                }
                // If the API returns a non-array (parse bug / transient HTML error), keep prior list.
            })
            .addCase(fetchSavedJobs.rejected, (state, action) => {
                state.savedJobsLoading = false;
                state.savedJobsError = action.payload;
                if (state.savedJobs.length === 0) {
                    const uid = getCurrentUserIdFromStorage();
                    const cached = uid ? clientDataCache.readSavedJobs(uid) : null;
                    if (cached?.length) {
                        state.savedJobs = cached;
                    }
                }
            })

            .addCase(deleteSavedJob.pending, (state) => {
                state.savedJobsLoading = true;
                state.savedJobsError = null;
            })
            .addCase(deleteSavedJob.fulfilled, (state, action) => {
                state.savedJobsLoading = false;
                state.savedJobs = state.savedJobs.filter(job => job._id !== action.payload);
                persistSavedJobsSnapshot(state);
            })
            .addCase(deleteSavedJob.rejected, (state, action) => {
                state.savedJobsLoading = false;
                state.savedJobsError = action.payload;
            })

            // checkJobIsSaved cases removed — thunk was calling a non-existent /is-saved route.
            
            .addCase(selectJobStrategy.pending, (state) => {
                state.savedJobsLoading = true;
                state.savedJobsError = null;
            })
            .addCase(selectJobStrategy.fulfilled, (state, action) => {
                state.savedJobsLoading = false;
                const updatedJob = action.payload;

                const normalizeJobId = (value) => {
                    if (value == null) return null;
                    if (typeof value === 'string') return value.trim() || null;
                    if (typeof value === 'object') {
                        const inner = value._id ?? value.id;
                        return inner != null ? String(inner) : null;
                    }
                    return String(value);
                };

                const updatedSavedJobId = updatedJob?._id != null ? String(updatedJob._id) : null;
                const updatedBaseJobId = normalizeJobId(updatedJob?.jobId);

                const index = state.savedJobs.findIndex((job) => {
                    const currentSavedJobId = job?._id != null ? String(job._id) : null;
                    const currentBaseJobId = normalizeJobId(job?.jobId);

                    if (updatedSavedJobId && currentSavedJobId === updatedSavedJobId) return true;
                    if (updatedBaseJobId && currentBaseJobId === updatedBaseJobId) return true;
                    return false;
                });

                if (index !== -1) {
                    const prev = state.savedJobs[index];
                    const merged = { ...prev, ...updatedJob };
                    const prevJob = prev?.jobId;
                    const incJob = updatedJob?.jobId;
                    const prevRich =
                        prevJob &&
                        typeof prevJob === 'object' &&
                        (prevJob.companyName || prevJob.jobPosition || prevJob.datentime);
                    const incBare =
                        incJob == null ||
                        typeof incJob === 'string' ||
                        (typeof incJob === 'object' &&
                            !(incJob.companyName || incJob.jobPosition || incJob.datentime));
                    if (prevRich && incBare) {
                        merged.jobId = prevJob;
                    }
                    state.savedJobs[index] = merged;
                }
                persistSavedJobsSnapshot(state);
            })
            .addCase(selectJobStrategy.rejected, (state, action) => {
                state.savedJobsLoading = false;
                state.savedJobsError = action.payload;
            });

        builder
            .addCase(clearJobStrategy.pending, (state) => {
                state.savedJobsLoading = true;
                state.savedJobsError = null;
            })
            .addCase(clearJobStrategy.fulfilled, (state, action) => {
                state.savedJobsLoading = false;
                const updatedJob = action.payload;
                if (!updatedJob) return;

                const updatedSavedJobId = updatedJob?._id != null ? String(updatedJob._id) : null;
                const updatedBaseJobId = updatedJob?.jobId != null ? String(updatedJob.jobId?._id || updatedJob.jobId) : null;

                const index = state.savedJobs.findIndex((job) => {
                    const currentSavedJobId = job?._id != null ? String(job._id) : null;
                    const currentBaseJobId = job?.jobId != null ? String(job.jobId?._id || job.jobId) : null;
                    if (updatedSavedJobId && currentSavedJobId === updatedSavedJobId) return true;
                    if (updatedBaseJobId && currentBaseJobId === updatedBaseJobId) return true;
                    return false;
                });

                if (index !== -1) {
                    const prev = state.savedJobs[index];
                    const merged = { ...prev, ...updatedJob };
                    if (prev?.jobId && typeof prev.jobId === 'object' && (!updatedJob?.jobId || typeof updatedJob.jobId === 'string')) {
                        merged.jobId = prev.jobId;
                    }
                    state.savedJobs[index] = merged;
                }

                persistSavedJobsSnapshot(state);
            })
            .addCase(clearJobStrategy.rejected, (state, action) => {
                state.savedJobsLoading = false;
                state.savedJobsError = action.payload;
            });
    }
});

export const { resetSavedJobsState } = savedJobsSlice.actions;

export default savedJobsSlice.reducer;